import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// --- Fix: prevent the "ResizeObserver loop" freeze on fullscreen / window resize ---
// recharts' ResponsiveContainer can trigger a ResizeObserver feedback loop when its
// flex parent resizes (e.g. entering fullscreen). That throws a benign error which
// the dev-server overlay renders full-screen, making the app appear stuck.
// Deferring RO callbacks to the next animation frame breaks the loop at the source.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

const RO_LOOP = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
const hideOverlay = () => {
  const el = document.getElementById("webpack-dev-server-client-overlay");
  if (el) el.style.display = "none";
  const div = document.getElementById("webpack-dev-server-client-overlay-div");
  if (div) div.style.display = "none";
};
// Continuously re-hide the CRA runtime-error overlay if it re-appears (e.g. the
// benign ResizeObserver loop on entering fullscreen) so the app never gets stuck.
if (typeof MutationObserver !== "undefined") {
  const mo = new MutationObserver(hideOverlay);
  const start = () => document.body && mo.observe(document.body, { childList: true, subtree: true });
  if (document.body) start();
  else window.addEventListener("DOMContentLoaded", start);
}
window.addEventListener(
  "error",
  (e) => {
    if (e.message && RO_LOOP.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      hideOverlay();
    }
  },
  true,
);
window.addEventListener("unhandledrejection", (e) => {
  if (e.reason && RO_LOOP.test(String(e.reason.message || e.reason))) {
    e.preventDefault();
    hideOverlay();
  }
});
// Re-hide on fullscreen change + nudge Leaflet/Recharts to re-measure cleanly.
document.addEventListener("fullscreenchange", () => {
  hideOverlay();
  window.requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>,
);

// Register the service worker so the public /go app is installable (PWA).
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/go-sw.js").catch(() => {});
  });
}
